import { forwardRef, useRef, useMemo } from 'react'
import type { JSX } from 'react'
import type { ComponentProps, HTMLAttributes, ReactNode } from 'react'

import { clsx } from 'clsx'

import { Notice } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { canPerform } from '~publish/utils/permissions/canPerform'

import { CommentHeader, CommentHeader_Channel } from './CommentHeader'
import { CommentText } from './CommentText'
import {
  CommentReactions,
  CommentReactions_Channel,
} from '../../components/CommentReactions'
import {
  EngagementAvatar,
  EngagementAvatar_Channel,
} from '../../components/EngagementAvatar'
import {
  EngagementItem,
  EngagementItemSkeleton,
} from '../../components/EngagementItem'
import { MentionedChannelText_Channel } from '../../components/MentionedChannelText'
import { StarRating } from '../../components/StarRating'
import {
  useCommentData,
  useCommentReactionError,
} from '../context/CommentContext'
import type { CommentData, LastReply } from '../fragments/commentData'
import { ViewOnlyIndicator } from './ViewOnlyIndicator'
import { isViewOnlyChannel } from '../../shared/viewOnlyScenario'
import type { ReplyFormStatus } from '../../types'
import { isPrivateLinkedInAccount } from '../../util/privateAccount'
import { useIsAllChannelsView } from '../../util/routeUtils'
import { CommentDeletedError } from '../CommentDeletedError'
import { CommentUnsupportedContent } from '../CommentUnsupportedContent'
import { CommentReplyForm } from '../CommentReplyForm'
import {
  CommentLastReplies,
  CommentLastReplies_Channel,
} from './CommentLastReplies'
import { CommentMedia } from '../CommentMedia'
import { usePrivateAccountPopover } from './hooks/usePrivateAccountPopover'
import { useCommentReplyForm } from '../CommentReplyForm/hooks/useCommentReplyForm'
import type { OptimisticReply } from '../../hooks/comments/useReplyToComment'

import styles from './CommentThreadContent.module.css'

export const CommentThreadContent_Channel = graphql(
  /* GraphQL */ `
    fragment CommentThreadContent_Channel on Channel @_unmask {
      service
      avatar
      allowedActions
      ...EngagementAvatar_Channel
      ...CommentHeader_Channel
      ...CommentReactions_Channel
      ...CommentLastReplies_Channel
      ...MentionedChannelText_Channel
    }
  `,
  [
    EngagementAvatar_Channel,
    CommentHeader_Channel,
    CommentReactions_Channel,
    CommentLastReplies_Channel,
    MentionedChannelText_Channel,
  ],
)

/**
 * The reply form still types its `channel` against the full codegen entity,
 * so the channel flowing through the card tree must satisfy both it and the
 * colocated fragment reads. The intersection collapses to the fragment type
 * once `CommentReplyForm` goes fully fragment-first.
 */
export type CommentThreadContentChannel = FragmentOf<
  typeof CommentThreadContent_Channel
> &
  ComponentProps<typeof CommentReplyForm>['channel']

/**
 * Merges local sentReplies with server lastReplies, deduplicating by ID.
 * sentReplies are newest-first, so they appear at the beginning of the array.
 * If a reply exists in both sentReplies and serverReplies, only the server version is shown.
 *
 * @param sentReplies - Local replies not yet in Apollo cache
 * @param serverReplies - Replies from the server (Apollo cache)
 * @returns Merged array with unique sentReplies first, then serverReplies
 */
const useMergedReplies = (
  sentReplies: Array<CommentData | LastReply | OptimisticReply>,
  serverReplies?: Array<LastReply | null> | null,
): Array<CommentData | LastReply | OptimisticReply | null> => {
  return useMemo(() => {
    // Create a Set of server reply IDs for O(1) lookups
    const serverReplyIds = new Set(
      serverReplies?.map((r) => r?.id).filter(Boolean) ?? [],
    )

    // Only include sentReplies that aren't already in server data
    const uniqueSentReplies = sentReplies.filter(
      (reply) => !serverReplyIds.has(reply.id),
    )

    return [...uniqueSentReplies, ...(serverReplies || [])]
  }, [sentReplies, serverReplies])
}

/**
 * Determines connector visibility for comment threads
 *
 * @param comment - The comment data
 * @param displayReplies - The merged array of replies (sentReplies + lastReplies)
 * @param replyStatus - The current reply form status
 * @param isContextComment - Whether this is a context comment
 * @param expanded - Whether the comment is expanded
 * @param isInterimExpanded - Whether in interim expanded state
 * @param isResolvedLocally - Whether the comment is resolved locally
 * @param canManage - Whether the actor can manage comments (a reply form can render below)
 * @returns Object with showParentLine and showConnector flags
 */
const getConnectorVisibility = (
  comment: CommentData,
  displayReplies: Array<CommentData | LastReply | OptimisticReply | null>,
  replyStatus: ReplyFormStatus,
  isContextComment: boolean,
  expanded: boolean,
  isInterimExpanded: boolean,
  isResolvedLocally: boolean,
  canManage: boolean,
): { showParentLine: boolean; showConnector: boolean } => {
  const isProcessing = replyStatus === 'processing'
  const isPublished = replyStatus === 'published'
  const hasParentComment = comment.parentId !== null
  const isResolved =
    comment.status === 'replied' ||
    comment.status === 'dismissed' ||
    isResolvedLocally

  // Show parent line when: context comment, OR expanded comment with parent
  const showParentLine =
    isContextComment ||
    (hasParentComment && expanded && (!isResolved || isInterimExpanded))

  // Connector shows when there's content below AND the comment is in a visible state
  const hasRepliesBelow = displayReplies.length > 0

  // The last term covers the reply form's own-avatar row below the comment.
  // View-only actors get no reply form, so without replies there is nothing
  // for the line to connect to and it must not render.
  const hasContentBelow =
    isContextComment ||
    hasRepliesBelow ||
    (canManage &&
      !isContextComment &&
      !comment.isDeleted &&
      (!isResolved || isProcessing || isInterimExpanded))

  const isVisibleState =
    expanded ||
    (isResolved && !isInterimExpanded) ||
    isProcessing ||
    isPublished

  const showConnector = hasContentBelow && isVisibleState

  return { showParentLine, showConnector }
}

type CommentThreadFrameProps = HTMLAttributes<HTMLElement> & {
  as?: 'article' | 'section'
  commentId: string
  status: string
  onlyComment: boolean
  expanded?: boolean
  children: ReactNode
}

export const CommentThreadFrame = forwardRef<
  HTMLElement,
  CommentThreadFrameProps
>(
  (
    {
      as: Component = 'section',
      commentId,
      status,
      onlyComment,
      expanded,
      className,
      children,
      ...props
    },
    ref,
  ): JSX.Element => (
    <Component
      ref={ref}
      className={clsx(
        styles.container,
        onlyComment && styles.onlyComment,
        !expanded && styles.collapsed,
        className,
      )}
      data-testid="comment-thread-content"
      data-comment-id={commentId}
      role={Component === 'section' ? 'group' : undefined}
      data-status={status}
      {...props}
    >
      <div className={styles.commentList}>{children}</div>
    </Component>
  ),
)
CommentThreadFrame.displayName = 'CommentThreadFrame'

const CommentThreadContentContainer = forwardRef<
  HTMLElement,
  HTMLAttributes<HTMLElement> & {
    isContextComment?: boolean
    expanded?: boolean
    children: ReactNode
  }
>(({ isContextComment, expanded, children, ...props }, ref): JSX.Element => {
  const comment = useCommentData()
  const hasParentComment = comment.parentId !== null

  const semanticLabel = isContextComment
    ? `Context comment from ${comment.author.username}`
    : `Comment thread from ${comment.author.username}`

  const statusText =
    comment.status === 'replied'
      ? 'replied to'
      : comment.status === 'dismissed'
        ? 'dismissed'
        : 'needs attention'

  return (
    <CommentThreadFrame
      ref={ref}
      commentId={comment.id}
      status={comment.status}
      onlyComment={!isContextComment && !hasParentComment}
      expanded={expanded}
      aria-label={`${semanticLabel} ${
        comment.isOwnReply ? '(your reply)' : ''
      } - ${statusText}`}
      {...props}
    >
      {children}
    </CommentThreadFrame>
  )
})
CommentThreadContentContainer.displayName = 'CommentThreadContentContainer'

type MainCommentProps = {
  channel: CommentThreadContentChannel
  isContextComment: boolean
  showParentLine: boolean
  showConnector: boolean
  expanded: boolean
}

/**
 * MainComment - Renders the primary comment item with avatar, header, text, and media
 */
const MainComment = ({
  channel,
  isContextComment,
  showParentLine,
  showConnector,
  expanded,
}: MainCommentProps): JSX.Element => {
  const comment = useCommentData()

  const isPrivateAccount = isPrivateLinkedInAccount({
    serviceType: channel.service,
    comment,
  })

  const { PrivateAccountPopover } = usePrivateAccountPopover()
  const isAllChannelsView = useIsAllChannelsView()

  // In the All Channels view, collapsed cards badge the author's avatar
  // with the source channel's service icon so you can scan both who
  // commented and which inbox it landed in.
  const showChannelBadge = isAllChannelsView && !expanded

  const avatar = (
    <EngagementAvatar
      showConnector={showConnector}
      hasParent={showParentLine}
      avatar={comment.isOwnReply ? channel.avatar : comment.author.avatar}
      username={comment.author.username}
      channel={showChannelBadge ? channel : undefined}
    />
  )

  // The private-account popover is tied to the comment author.
  const wrappedAvatar = isPrivateAccount ? (
    <PrivateAccountPopover>{avatar}</PrivateAccountPopover>
  ) : (
    avatar
  )

  return (
    <EngagementItem
      variant={isContextComment ? 'context' : 'root'}
      aria-labelledby={`comment-header-${comment.id}`}
      data-testid="comment-item"
      data-replied={comment.status === 'replied' ? 'true' : 'false'}
      data-dismissed={comment.status === 'dismissed' ? 'true' : 'false'}
      avatar={wrappedAvatar}
    >
      <CommentHeader isContextComment={isContextComment} channel={channel} />
      {comment.metadata?.userRating != null && !comment.isOwnReply && (
        <StarRating rating={comment.metadata.userRating} />
      )}
      <CommentText channel={channel} />
      <CommentMedia />
      {comment.contentType === 'unsupported' && !comment.isDeleted && (
        <CommentUnsupportedContent />
      )}
      {comment.isDeleted && <CommentDeletedError />}
      <CommentReactions channel={channel} />
    </EngagementItem>
  )
}

const ReplyErrorMessage = ({
  errorMessage,
}: {
  errorMessage?: string
}): JSX.Element => {
  return (
    <Notice
      variant="error"
      data-error-message="true"
      className={styles.errorMessage}
    >
      <Notice.Text>{errorMessage}</Notice.Text>
    </Notice>
  )
}

const ReactionErrorMessage = ({
  errorMessage,
}: {
  errorMessage: string
}): JSX.Element => {
  return (
    <Notice variant="error" className={styles.errorMessage}>
      <Notice.Text>{errorMessage}</Notice.Text>
    </Notice>
  )
}

type CommentThreadContentProps = {
  channel: CommentThreadContentChannel
  isContextComment?: boolean
  onExpandNextComment?: (
    fromCommentId?: string,
    onlyRepliable?: boolean,
  ) => void
  expanded?: boolean
  onCollapse?: () => void
  onReplyStatusChange?: (status: ReplyFormStatus) => void
  isInterimExpanded?: boolean
  isResolvedLocally?: boolean
}

export const CommentThreadContent = forwardRef<
  HTMLElement,
  CommentThreadContentProps
>(
  (
    {
      channel,
      isContextComment = false,
      onExpandNextComment,
      expanded = false,
      onCollapse,
      onReplyStatusChange,
      isInterimExpanded = false,
      isResolvedLocally = false,
    },
    ref,
  ): JSX.Element => {
    const comment = useCommentData()
    const { reactionError } = useCommentReactionError()
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    const reply = useCommentReplyForm({
      channel,
      textareaRef,
      onExpandNextComment,
      onReplyStatusChange,
      expanded,
      onCollapse,
      isResolvedLocally,
    })

    const isResolved =
      comment.status === 'replied' || comment.status === 'dismissed'

    const canManage = canPerform(channel, 'manageComments')

    const canReply =
      !isContextComment &&
      !comment.isDeleted &&
      (!isResolved || isInterimExpanded) &&
      canManage

    // A view-only card surfaces the "View only" badge (bottom-right, inside
    // the card) once expanded — collapsed cards stay clean. Context comments
    // are excluded — they're ancestors shown for context, not the actor's own
    // actionable comment.
    const showViewOnlyIndicator =
      expanded && !isContextComment && isViewOnlyChannel(channel)

    // Merge local sentReplies with server lastReplies
    const displayReplies = useMergedReplies(
      reply.sentReplies,
      comment.lastReplies,
    )

    // Get connector visibility for parent line and bottom connector
    const { showParentLine, showConnector } = getConnectorVisibility(
      comment,
      displayReplies,
      reply.status,
      isContextComment,
      expanded,
      isInterimExpanded,
      isResolvedLocally,
      canManage,
    )

    const showErrorMessage = reply.status === 'error' && !expanded

    return (
      <CommentThreadContentContainer
        key={comment.id}
        ref={ref}
        isContextComment={isContextComment}
        expanded={expanded}
      >
        {showErrorMessage && <ReplyErrorMessage errorMessage={reply.error} />}
        {reactionError && <ReactionErrorMessage errorMessage={reactionError} />}
        <MainComment
          channel={channel}
          isContextComment={isContextComment}
          showParentLine={showParentLine}
          showConnector={showConnector}
          expanded={expanded}
        />

        <CommentLastReplies
          channel={channel}
          lastReplies={displayReplies}
          isProcessing={reply.status === 'processing'}
          isFormVisible={isContextComment || (expanded && canReply)}
        />

        <CommentReplyForm
          channel={channel}
          reply={reply}
          textareaRef={textareaRef}
          expanded={expanded}
          canReply={canReply}
        />

        {showViewOnlyIndicator && <ViewOnlyIndicator />}
      </CommentThreadContentContainer>
    )
  },
)

CommentThreadContent.displayName = 'CommentThreadContent'

export const CommentThreadContentSkeleton = (): JSX.Element => {
  return (
    <section className={clsx(styles.container)}>
      <div className={styles.commentList}>
        <EngagementItemSkeleton />
      </div>
    </section>
  )
}
